import { Info, Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// The per-queue Tests tab: the orderable-test catalogue behind the Doctor Queue's "Order tests"
// sheet. Categories are the headings the doctor scans; tests are the chips under them.
//
// Unlike the flat entry-type / consultation-mode lists, tests are NESTED under categories, so this
// section renders a card per category rather than reusing EntryTypesSection's sortable row list —
// dragging a test between categories is not a reorder, it is a re-parenting, and the doctor's
// sheet reads the category order, not a global one.

const EYE_OPTIONS = [
  { value: '', label: 'Test' },
  { value: 'right', label: 'Dilate R' },
  { value: 'left', label: 'Dilate L' },
  { value: 'both', label: 'Dilate both' },
];

function TestRow({ test, disabled, onChange, onDelete }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        value={test.name}
        onChange={(e) => onChange({ name: e.target.value })}
        maxLength={80}
        disabled={disabled}
        className="h-8 flex-1 text-sm"
        placeholder="Test name"
      />
      <div className="flex shrink-0 items-center gap-1 rounded-md bg-muted/60 p-0.5">
        {EYE_OPTIONS.map((option) => (
          <button
            key={option.value || 'plain'}
            type="button"
            disabled={disabled}
            onClick={() => onChange({ dilationEye: option.value })}
            title={
              option.value
                ? 'Dilation tests render as one Right/Left/Both control in the doctor’s sheet, not as separate chips'
                : 'An ordinary test — one chip in the doctor’s sheet'
            }
            className={cn(
              'rounded px-2 py-1 text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
              (test.dilationEye || '') === option.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        aria-label={`Delete test ${test.name}`}
        className={cn(
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
        )}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  );
}

export function TestsSection({
  testCategories,
  tests,
  disabled = false,
  onChangeCategory,
  onDeleteCategory,
  onAddCategory,
  onChangeTest,
  onDeleteTest,
  onAddTest,
  onResetCatalogue,
}) {
  const configured = testCategories.length > 0 && tests.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex gap-2.5 rounded-lg bg-primary/10 px-4 py-3 ring-1 ring-primary/25">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
        <p className="text-[12.5px] leading-relaxed text-foreground">
          The tests a doctor can order from this queue, grouped into the headings they scan.{' '}
          {configured ? (
            <>
              Leave both lists empty to fall back to the built-in ophthalmology catalogue.
            </>
          ) : (
            <>
              This queue has none configured, so doctors see the built-in ophthalmology catalogue.
              Add a category to override it.
            </>
          )}
        </p>
      </div>

      <div className="space-y-3">
        {testCategories.map((category) => {
          const rows = tests.filter((t) => t.categoryId === category.id);
          return (
            <div key={category.id} className="rounded-lg border border-border bg-card p-3 shadow-sm">
              <div className="mb-2.5 flex items-center gap-2">
                <Input
                  value={category.name}
                  onChange={(e) => onChangeCategory(category.id, { name: e.target.value })}
                  maxLength={60}
                  disabled={disabled}
                  className="h-8 w-72 text-sm font-semibold"
                  placeholder="Category name"
                />
                <span className="text-[11.5px] font-semibold text-muted-foreground">
                  {rows.length} {rows.length === 1 ? 'test' : 'tests'}
                </span>
                <div className="flex-1" />
                <button
                  type="button"
                  onClick={() => onDeleteCategory(category.id)}
                  disabled={disabled}
                  aria-label={`Delete category ${category.name}`}
                  title="Deleting a category also deletes the tests filed under it"
                  className={cn(
                    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-transparent text-muted-foreground transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive',
                  )}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>

              <div className="space-y-2">
                {rows.map((test) => (
                  <TestRow
                    key={test.id}
                    test={test}
                    disabled={disabled}
                    onChange={(patch) => onChangeTest(test.id, patch)}
                    onDelete={() => onDeleteTest(test.id)}
                  />
                ))}
              </div>

              {rows.length === 0 ? (
                <div className="rounded-md border border-dashed border-border px-3 py-4 text-center text-xs text-muted-foreground">
                  No tests in this category yet.
                </div>
              ) : null}

              {!disabled ? (
                <button
                  type="button"
                  onClick={() => onAddTest(category.id)}
                  className="mt-2 inline-flex w-fit items-center gap-2 rounded-lg border border-dashed border-border px-3 py-1.5 text-[12px] font-semibold text-muted-foreground transition hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  Add test
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {testCategories.length === 0 ? (
        <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
          No categories yet — doctors see the built-in catalogue.
        </div>
      ) : null}

      {!disabled ? (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onAddCategory}
            className="inline-flex w-fit items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-[12.5px] font-semibold text-muted-foreground transition hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Add category
          </button>
          <button
            type="button"
            onClick={onResetCatalogue}
            title="Replace the list with the built-in ophthalmology catalogue, ready to edit"
            className="inline-flex w-fit items-center gap-2 rounded-lg border border-border px-3 py-2 text-[12.5px] font-semibold text-muted-foreground transition hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Start from the built-in catalogue
          </button>
        </div>
      ) : null}
    </div>
  );
}
